/**
 * Login Page
 * Google OAuth sign-in restricted to peckschools.net domain
 * Age verification and onboarding are handled separately
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    signInWithGoogle,
    checkUserExists,
    updateLastLogin,
} from '../lib/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { isAdminEmail } from '../lib/auth';

export default function LoginPage() {
    const navigate = useNavigate();
    const { user, userDoc, loading: authLoading, onboardingCompleted } = useAuth();

    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    // Redirect if already logged in
    useEffect(() => {
        if (!authLoading && user && userDoc) {
            if (onboardingCompleted) {
                navigate('/app');
            } else {
                navigate('/onboarding');
            }
        }
    }, [user, userDoc, authLoading, onboardingCompleted, navigate]);

    const handleGoogleSignIn = async () => {
        setError(null);
        setLoading(true);

        try {
            const firebaseUser = await signInWithGoogle();

            // Check if user already exists in Firestore
            const exists = await checkUserExists(firebaseUser.uid);

            if (exists) {
                // Existing user - update last login
                await updateLastLogin(firebaseUser.uid);
                // AuthContext will handle redirect based on onboarding status
            } else {
                // New user - create minimal user document
                // Full onboarding will be done on the onboarding page
                await setDoc(doc(db, 'users', firebaseUser.uid), {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email || '',
                    displayName: firebaseUser.displayName || '',
                    photoURL: firebaseUser.photoURL || null,
                    role: isAdminEmail(firebaseUser.email) ? 'admin' : 'student',
                    birthYear: 0, // Placeholder - will be set during onboarding
                    ageVerified: false,
                    onboardingCompleted: false,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    lastLoginAt: serverTimestamp(),
                });
                // Navigate to onboarding
                navigate('/onboarding');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to sign in with Google');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
                <div className="card animate-fade-in">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-500 rounded-xl mb-4">
                            <span className="text-white font-display font-bold text-3xl">🏴‍☠️</span>
                        </div>
                        <h1 className="text-2xl font-display font-bold text-white">
                            Welcome Back
                        </h1>
                        <p className="text-secondary-400 mt-2">
                            Sign in with your peckschools.net account
                        </p>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div id="login-error" role="alert" className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
                            <p className="text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Google Sign In Button */}
                    <button
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                        aria-describedby={error ? 'login-error' : undefined}
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white hover:bg-gray-100 text-gray-800 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" role="status" aria-label="Signing in" />
                        ) : (
                            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    fill="currentColor"
                                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                />
                                <path
                                    fill="currentColor"
                                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                />
                            </svg>
                        )}
                        Continue with Google
                    </button>

                    <p className="mt-6 text-xs text-secondary-500 text-center">
                        Only @peckschools.net accounts can sign in.
                        <br />
                        First-time users will go through a quick setup.
                    </p>
                </div>
            </div>
        </div>
    );
}
